import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const slugify = (n) => (n || "").toLowerCase().replace(/\s+/g, "-");

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [slots, setSlots] = useState([null, null]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const gridRef = useRef(null);

  useEffect(() => {
    api
      .get("/products")
      .then((res) => {
        setProducts(res.data);
        const model = new URLSearchParams(location.search).get("model");
        let first = res.data[0]?.id ?? null;
        let second = res.data[1]?.id ?? res.data[0]?.id ?? null;
        if (model) {
          const match = res.data.find((p) => slugify(p.name) === model);
          if (match) {
            first = match.id;
            const other = res.data.find((p) => p.id !== match.id);
            second = other?.id ?? match.id;
          }
        }
        setSlots([first, second]);
      })
      .finally(() => setLoading(false));
  }, [location.search]);

  useEffect(() => {
    if (!loading && new URLSearchParams(location.search).get("model") && gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading, location.search]);

  const setSlot = (idx) => (id) =>
    setSlots((prev) => prev.map((v, i) => (i === idx ? id : v)));

  const byId = (id) => products.find((p) => p.id === id);

  return (
    <div data-testid="shop-page" className="pt-28 pb-24 md:pt-36 md:pb-32">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Compare the lineup</p>
          <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter text-zinc-900">
            Choose your Vsense.
          </h1>
          <p className="mt-5 text-base md:text-lg text-zinc-500 leading-relaxed">
            Two models, crafted for different lives. Reserve your pair in seconds —
            free, with no credit card required.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-1.5 rounded-full">
            <ShieldCheck className="w-4 h-4" /> Free reservation · No payment info needed
          </div>
        </motion.div>

        {loading ? (
          <div className="mt-16 grid md:grid-cols-2 gap-12">
            {[0, 1].map((i) => (
              <div key={i} className="rounded-3xl bg-[#F5F5F7] h-[560px] animate-pulse" />
            ))}
          </div>
        ) : (
          <div ref={gridRef} id="products" className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-6 md:divide-x md:divide-zinc-100 scroll-mt-28">
            {slots.map((id, i) => {
              const product = byId(id);
              if (!product) return null;
              return (
                <ProductCard
                  key={`${i}-${product.id}`}
                  product={product}
                  allProducts={products}
                  index={i}
                  onChangeModel={setSlot(i)}
                />
              );
            })}
          </div>
        )}

        {/* App pairing banner */}
        <div className="mt-24 md:mt-32 rounded-[2rem] bg-zinc-950 text-white overflow-hidden">
          <div className="grid md:grid-cols-2 items-center gap-8 p-8 md:p-14">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Included with every pair</p>
              <h2 className="font-display text-2xl md:text-4xl font-extrabold tracking-tighter leading-tight">
                Every Vsense pairs with the guardian app.
              </h2>
              <p className="mt-4 text-sm md:text-base text-white/60 max-w-md leading-relaxed">
                Live streaming, GPS location, two-way voice, and one-tap SOS — set up in minutes,
                free for the whole family.
              </p>
            </div>
            <div className="flex justify-center md:justify-end">
              <img
                src="/products/app-phones.jpg"
                alt="The Vsense guardian app"
                className="w-full max-w-sm rounded-2xl"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
